import * as Popover from '@radix-ui/react-popover';
import { Bell } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useMarkAllRead, useMarkRead } from '../../features/notifications/mutations';
import { useNotifications, useUnreadCount } from '../../features/notifications/queries';
import type { NotificationDto } from '../../lib/api/endpoints';
import { relativeTimestamp } from '../../lib/format/date';
import { Button } from '../ui/Button';

const DESTINATIONS = {
  BREACH: '/trade-plan',
  WEEKLY_REVIEW: '/reviews/weekly',
  IMPORT_DONE: '/import',
  JOURNAL_REQUIRED: '/journal',
  REVIEW_PENDING: '/reviews',
  RULE_CHANGE: '/rules',
  PRICE_ALERT: '/dashboard',
} as const;

export function NotificationCenter() {
  const navigate = useNavigate();
  const notifications = useNotifications({ limit: 20 });
  const unreadCount = useUnreadCount();
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const count = unreadCount.data ?? 0;

  function openNotification(notification: NotificationDto) {
    markRead.mutate(notification.id, {
      onSuccess: () =>
        navigate(
          notification.kind === 'REVIEW_PENDING' && notification.tradeId
            ? `/reviews/${notification.tradeId}`
            : DESTINATIONS[notification.kind],
        ),
    });
  }

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-surface text-text transition hover:border-primary/50 hover:text-primary"
          aria-label="Open notifications"
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {count > 9 ? (
            <span className="absolute -right-1 -top-1 min-w-5 rounded-full bg-danger px-1 text-center text-[10px] font-semibold text-white">
              {count}
            </span>
          ) : count > 0 ? (
            <span className="absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full bg-danger" />
          ) : null}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-[min(92vw,380px)] rounded border border-border bg-bg p-0 shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="text-sm font-semibold">Notifications</h2>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={markAllRead.isPending || count === 0}
              onClick={() => markAllRead.mutate()}
            >
              Mark all read
            </Button>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {notifications.isLoading ? (
              <p className="px-4 py-6 text-sm text-text-muted">Loading notifications...</p>
            ) : notifications.data && notifications.data.length > 0 ? (
              <ul className="divide-y divide-border">
                {notifications.data.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      className="grid w-full grid-cols-[1.5rem_1fr] gap-3 px-4 py-3 text-left hover:bg-surface"
                      onClick={() => openNotification(notification)}
                    >
                      <span className="mt-0.5" aria-hidden="true">
                        {severityIcon(notification.severity)}
                      </span>
                      <span className="min-w-0">
                        <span
                          className={`block truncate text-sm ${
                            notification.readAt ? 'font-medium' : 'font-semibold'
                          }`}
                        >
                          {notification.title}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-text-muted">
                          {truncate(notification.body)}
                        </span>
                        <span className="mt-1 block text-xs text-text-muted">
                          {relativeTimestamp(notification.createdAt)}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="px-4 py-8 text-center text-sm text-text-muted">No notifications yet.</p>
            )}
          </div>
          <Popover.Arrow className="fill-bg" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function severityIcon(severity: NotificationDto['severity']): string {
  if (severity === 'BLOCK') return '🔴';
  if (severity === 'WARN') return '🟡';
  return '🔵';
}

function truncate(value: string): string {
  return value.length > 80 ? `${value.slice(0, 77)}...` : value;
}
